import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';

const NotificationToast = ({ 
  notifications = [], 
  onDismiss,
  maxVisible = 3 
}) => {
  const [visibleNotifications, setVisibleNotifications] = useState([]);

  useEffect(() => {
    setVisibleNotifications(notifications.slice(0, maxVisible));
  }, [notifications, maxVisible]);

  const getNotificationConfig = (type) => {
    switch (type) {
      case 'success':
        return {
          icon: 'CheckCircle',
          iconColor: 'var(--color-success)',
          bgColor: 'bg-white',
          borderColor: 'border-success',
          textColor: 'text-success',
          titleColor: 'text-foreground'
        };
      case 'error':
        return {
          icon: 'AlertCircle',
          iconColor: 'var(--color-error)',
          bgColor: 'bg-white',
          borderColor: 'border-error',
          textColor: 'text-error',
          titleColor: 'text-foreground'
        };
      case 'warning':
        return {
          icon: 'AlertTriangle',
          iconColor: 'var(--color-warning)',
          bgColor: 'bg-white',
          borderColor: 'border-warning',
          textColor: 'text-warning',
          titleColor: 'text-foreground'
        };
      case 'info':
      default:
        return {
          icon: 'Info',
          iconColor: 'var(--color-primary)',
          bgColor: 'bg-white',
          borderColor: 'border-primary',
          textColor: 'text-primary',
          titleColor: 'text-foreground'
        };
    }
  };

  const handleDismiss = (notificationId) => {
    if (onDismiss) {
      onDismiss(notificationId);
    }
  };

  const handleActionClick = (action, notificationId) => {
    if (action.onClick) {
      action.onClick();
    }
    handleDismiss(notificationId);
  };

  if (visibleNotifications.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-24 right-6 z-50 space-y-3 max-w-sm w-full">
      {visibleNotifications.map((notification, index) => {
        const config = getNotificationConfig(notification.type);
        
        return (
          <div
            key={notification.id}
            className={`
              ${config.bgColor} ${config.borderColor} border-2 rounded-lg shadow-lg
              transform transition-all duration-300 ease-out
              opacity-100 scale-100
            `}
            style={{ 
              animationDelay: `${index * 100}ms`,
              transform: `translateY(${index * 4}px)` 
            }}
          >
            <div className="p-4">
              <div className="flex items-start space-x-3">
                {/* Notification Icon */}
                <div className="flex-shrink-0 mt-0.5">
                  <Icon 
                    name={config.icon} 
                    size={20} 
                    color={config.iconColor}
                    strokeWidth={2}
                  />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {notification.title && (
                    <div className={`font-semibold ${config.titleColor} mb-1`}>
                      {notification.title}
                    </div>
                  )}
                  <div className="text-sm text-muted-foreground leading-relaxed">
                    {notification.message}
                  </div>

                  {/* Action Button */}
                  {notification.action && (
                    <button
                      onClick={() => handleActionClick(notification.action, notification.id)}
                      className={`
                        mt-3 px-3 py-1.5 rounded-md text-sm font-medium
                        ${config.textColor} hover:bg-muted
                        transition-colors duration-200
                        border-2 border-current/20 hover:border-current/30
                      `}
                    >
                      {notification.action.label}
                    </button>
                  )}
                </div>

                {/* Dismiss Button */}
                <button
                  onClick={() => handleDismiss(notification.id)}
                  className="
                    flex-shrink-0 p-1 rounded-md hover:bg-muted
                    text-muted-foreground hover:text-foreground
                    transition-colors duration-200
                  "
                  aria-label="Dismiss notification"
                >
                  <Icon name="X" size={16} color="currentColor" />
                </button>
              </div>

              {/* Progress Bar for Timed Notifications */}
              {notification.duration && notification.duration > 0 && (
                <div className="mt-3 -mb-1">
                  <div className="w-full bg-muted rounded-full h-1 overflow-hidden">
                    <div 
                      className={`h-full ${config.textColor.replace('text-', 'bg-')} rounded-full`}
                      style={{
                        animation: `shrink ${notification.duration}ms linear forwards`
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Overflow Indicator */}
      {notifications.length > maxVisible && (
        <div className="text-center">
          <div className="inline-flex items-center space-x-2 px-3 py-2 bg-white border-2 border-border rounded-full text-sm font-medium text-muted-foreground">
            <Icon name="MoreHorizontal" size={16} color="currentColor" />
            <span>+{notifications.length - maxVisible} more</span>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
};

// Individual Toast Component for single notifications
export const Toast = ({ 
  type = 'info', 
  title, 
  message, 
  action, 
  duration = 4000,
  onDismiss 
}) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => {
          if (onDismiss) onDismiss();
        }, 300); // Wait for exit animation
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, onDismiss]);

  if (!isVisible) {
    return null;
  }

  const config = {
    success: {
      icon: 'CheckCircle',
      iconColor: 'var(--color-success)',
      bgColor: 'bg-white',
      borderColor: 'border-success',
      textColor: 'text-success',
      titleColor: 'text-foreground'
    },
    error: {
      icon: 'AlertCircle',
      iconColor: 'var(--color-error)',
      bgColor: 'bg-white',
      borderColor: 'border-error',
      textColor: 'text-error',
      titleColor: 'text-foreground'
    },
    warning: {
      icon: 'AlertTriangle',
      iconColor: 'var(--color-warning)',
      bgColor: 'bg-white',
      borderColor: 'border-warning',
      textColor: 'text-warning',
      titleColor: 'text-foreground'
    },
    info: {
      icon: 'Info',
      iconColor: 'var(--color-primary)',
      bgColor: 'bg-white',
      borderColor: 'border-primary',
      textColor: 'text-primary',
      titleColor: 'text-foreground'
    }
  }[type];

  return (
    <div className="fixed top-24 right-6 z-50 max-w-sm w-full">
      <div className={`
        ${config.bgColor} ${config.borderColor} border-2 rounded-lg shadow-lg
        transform transition-all duration-300 ease-out
        ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
      `}>
        <div className="p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 mt-0.5">
              <Icon 
                name={config.icon} 
                size={20} 
                color={config.iconColor}
                strokeWidth={2}
              />
            </div>

            <div className="flex-1 min-w-0">
              {title && (
                <div className={`font-semibold ${config.titleColor} mb-1`}>
                  {title}
                </div>
              )}
              <div className="text-sm text-muted-foreground leading-relaxed">
                {message}
              </div>

              {action && (
                <button
                  onClick={() => {
                    if (action.onClick) action.onClick();
                    setIsVisible(false);
                  }}
                  className={`
                    mt-3 px-3 py-1.5 rounded-md text-sm font-medium
                    ${config.textColor} hover:bg-muted
                    transition-colors duration-200
                    border-2 border-current/20 hover:border-current/30
                  `}
                >
                  {action.label}
                </button>
              )}
            </div>

            <button
              onClick={() => setIsVisible(false)}
              className="
                flex-shrink-0 p-1 rounded-md hover:bg-muted
                text-muted-foreground hover:text-foreground
                transition-colors duration-200
              "
              aria-label="Dismiss notification"
            >
              <Icon name="X" size={16} color="currentColor" />
            </button>
          </div>

          {duration > 0 && (
            <div className="mt-3 -mb-1">
              <div className="w-full bg-muted rounded-full h-1 overflow-hidden">
                <div 
                  className={`h-full ${config.textColor.replace('text-', 'bg-')} rounded-full`}
                  style={{
                    animation: `shrink ${duration}ms linear forwards`
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
};

export default NotificationToast;